import { usePathname } from "expo-router";
import type { ReactNode } from "react";
import { ActivityIndicator, Platform, ScrollView, View } from "react-native";
import { AuthForm } from "~/components/composed/AuthForm";
import { ExportData } from "~/components/composed/ExportData";
import { DeleteAccount } from "~/components/composed/DeleteAccount";
import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import { Button } from "~/components/ui/Button";
import { RuleLine } from "~/components/ui/RuleLine";
import { Screen } from "~/components/ui/Screen";
import {
  BodyText,
  CaptionText,
  Heading2,
  Heading3,
} from "~/components/ui/Text";
import { useAuth } from "~/hooks/useAuth";
import {
  DURANCE_MONTHLY_PRICE_TEXT,
  DURANCE_TRIAL_DAYS,
} from "~/services/revenuecat";
import { isWebPublicPath, webAccessState } from "~/services/web-access";
import { spacing, useTheme } from "~/styles/theme";

const CONTENT_MAX_WIDTH = 440;

function GateFrame({ children }: { children: ReactNode }) {
  return (
    <Screen>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: "center",
          paddingVertical: spacing.xl,
          paddingHorizontal: spacing.lg,
        }}
      >
        <View
          style={{
            width: "100%",
            maxWidth: CONTENT_MAX_WIDTH,
            alignSelf: "center",
            gap: spacing.lg,
          }}
        >
          <View style={{ gap: spacing.md }}>
            <Heading3 style={{ letterSpacing: -0.5 }}>DURANCE</Heading3>
            <RuleLine strong />
          </View>
          {children}
        </View>
      </ScrollView>
    </Screen>
  );
}

function WebSignInScreen() {
  const { colors } = useTheme();
  return (
    <GateFrame>
      <View style={{ gap: spacing.xs }}>
        <Heading2>Sign in to Durance</Heading2>
        <BodyText color={colors.muted}>
          Durance on the web needs an account. New here? Create one and start
          your {DURANCE_TRIAL_DAYS}-day free trial — then{" "}
          {DURANCE_MONTHLY_PRICE_TEXT}/month. Cancel any time.
        </BodyText>
      </View>
      <AuthForm />
    </GateFrame>
  );
}

function WebSubscribeScreen() {
  const { colors } = useTheme();
  const { user, signOut } = useAuth();
  return (
    <GateFrame>
      <View style={{ gap: spacing.xs }}>
        <Heading2>Start your free trial</Heading2>
        {user ? (
          <CaptionText color={colors.muted}>
            Signed in as {user.email}
          </CaptionText>
        ) : null}
      </View>
      <UpgradePrompt variant="required" />
      <RuleLine />
      <Button
        label="Sign out"
        variant="secondary"
        fullWidth
        onPress={() => void signOut()}
      />
      <RuleLine />
      <ExportData />
      <DeleteAccount />
    </GateFrame>
  );
}

/**
 * Durance on the web has no local-only mode: a visitor must sign in (or create
 * an account) and hold an active subscription — a free trial counts — before
 * the app renders. Native platforms pass straight through. The decision
 * itself lives in `~/services/web-access` so it can be unit-tested.
 *
 * Children (the router's Stack and the counters state) mount only once access
 * is granted, so no guest counters are ever created or read on web.
 */
export function WebAccessGate({ children }: { children: ReactNode }) {
  const { colors } = useTheme();
  const { user, ready, configured } = useAuth();
  const pathname = usePathname();
  const state = webAccessState({
    platform: Platform.OS,
    authConfigured: configured,
    authReady: ready,
    userId: user?.id ?? null,
    hasSyncAccess: user?.hasSyncAccess ?? false,
    publicPath: isWebPublicPath(pathname),
  });

  if (state === "open") return <>{children}</>;
  if (state === "loading") {
    return (
      <Screen>
        <View
          style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        >
          <ActivityIndicator color={colors.muted} />
        </View>
      </Screen>
    );
  }
  return state === "sign-in" ? <WebSignInScreen /> : <WebSubscribeScreen />;
}
